import { Chip, IconButton, Stack, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";

import AppDataTable from "../../../../components/common/AppDataTable";
import { PurchaseBill } from "../types";

interface PurchaseBillTableProps {
  rows: PurchaseBill[];
  loading: boolean;
  page: number;
  pageSize: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onEdit: (purchaseBill: PurchaseBill) => void;
  onView: (purchaseBill: PurchaseBill) => void;
  onReceive: (purchaseBill: PurchaseBill) => void;
  onComplete: (purchaseBill: PurchaseBill) => void;
  onCancel: (purchaseBill: PurchaseBill) => void;
  onDelete: (purchaseBill: PurchaseBill) => void;
}

const statusColor = (status: PurchaseBill["status"]) => {
  if (status === "COMPLETED") return "success";
  if (status === "REGISTERED") return "info";
  if (status === "RECEIVED") return "warning";
  if (status === "CANCELLED") return "error";
  return "default";
};

const PurchaseBillTable = ({
  rows,
  loading,
  page,
  pageSize,
  rowCount,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onView,
  onReceive,
  onComplete,
  onCancel,
  onDelete,
}: PurchaseBillTableProps) => {
  const columns: GridColDef[] = [
    {
      field: "code",
      headerName: "Bill Code",
      width: 140,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice No",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "supplierName",
      headerName: "Supplier",
      flex: 1.3,
      minWidth: 200,
    },
    {
      field: "warehouseName",
      headerName: "Receiving Hub",
      flex: 1,
      minWidth: 180,
      valueGetter: (params) =>
        params.row.warehouseName || params.row.warehouseId || "-",
    },
    {
      field: "purchaseDate",
      headerName: "Purchase Date",
      width: 150,
      valueGetter: (params) =>
        params.row.purchaseDate
          ? new Date(params.row.purchaseDate).toLocaleDateString()
          : "-",
    },
    {
      field: "totalItems",
      headerName: "Items",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      width: 140,
      valueFormatter: ({ value }) => Number(value || 0).toFixed(2),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={statusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 240,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isDraft = params.row.status === "DRAFT";
        const canComplete =
          params.row.status === "DRAFT" || params.row.status === "RECEIVED";
        const isCancelled = params.row.status === "CANCELLED";

        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Edit">
              <span>
                <IconButton
                  size="small"
                  disabled={!isDraft}
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="View Details">
              <IconButton
                size="small"
                color="info"
                onClick={(event) => {
                  event.stopPropagation();
                  onView(params.row);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Mark Received">
              <span>
                <IconButton
                  size="small"
                  color="success"
                  disabled={!isDraft}
                  onClick={(event) => {
                    event.stopPropagation();
                    onReceive(params.row);
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Complete">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={!canComplete}
                  onClick={(event) => {
                    event.stopPropagation();
                    onComplete(params.row);
                  }}
                >
                  <TaskAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Cancel">
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={isCancelled}
                  onClick={(event) => {
                    event.stopPropagation();
                    onCancel(params.row);
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(params.row);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <AppDataTable
      rows={rows}
      columns={columns}
      loading={loading}
      page={page}
      pageSize={pageSize}
      rowCount={rowCount}
      onRowClick={(params) => onView(params.row)}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      sx={{
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },
      }}
    />
  );
};

export default PurchaseBillTable;
